import * as React from 'react';
import { forwardRef, Ref } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { stopEvent } from 'utils/event';
import { KeyboardEventCode } from 'utils/key_codes';

export type KCFMenuItemProps = Readonly<{
    /**
     * The actual text of the menu item (not html title)
     */
    title: string;
    /**
     * The hover text of the menu item (html title). Defaults to `title`.
     */
    hoverTitle?: string;
    icon: JSX.Element;
    hoverIcon: JSX.Element;
    disabled?: boolean;
    onSelect: (
        event:
            | React.MouseEvent<HTMLDivElement, MouseEvent>
            | React.KeyboardEvent<HTMLDivElement>,
    ) => void;
    ariaLabel?: string;
}>;

const MenuItem = styled.div<{ $disabled?: boolean }>`
    width: auto;
    border: 1px solid ${tokens.border.default};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    color: ${({ $disabled }) => ($disabled ? tokens.text.disabled : tokens.text.primary)};
    font-size: 14px;

    &:not(:first-child) {
        border-top: 0;
    }

    & .default_icon {
        display: initial;
    }
    & .hover_icon {
        display: none;
    }
    &[aria-disabled='true'] {
        & .default_icon,
        & .hover_icon {
            opacity: ${tokens.action.disabledOpacity};
        }
    }
    &:hover:not([aria-disabled='true']),
    &:focus-visible:not([aria-disabled='true']) {
        & .default_icon {
            display: none;
        }
        & .hover_icon {
            display: initial;
        }
    }
    &:active {
        background-color: ${tokens.background.subtle};
    }
`;

function KCFMenuItem(
    {
        title,
        hoverTitle = title,
        icon,
        hoverIcon,
        disabled,
        onSelect,
        ariaLabel,
    }: KCFMenuItemProps,
    ref: Ref<HTMLDivElement>,
) {
    const handleSelect = (
        event:
            | React.MouseEvent<HTMLDivElement, MouseEvent>
            | React.KeyboardEvent<HTMLDivElement>,
    ) => {
        stopEvent(event);
        if (!disabled) {
            onSelect(event);
        }
    };

    return (
        <MenuItem
            ref={ref}
            aria-label={ariaLabel}
            aria-disabled={disabled}
            title={hoverTitle}
            $disabled={disabled}
            role="menuitem"
            className="flex-row justify-between align-center p-10"
            onClick={handleSelect}
            onKeyDown={(e) => {
                const code = e.code as KeyboardEventCode;
                if (
                    code === KeyboardEventCode.Space ||
                    code === KeyboardEventCode.Enter
                ) {
                    handleSelect(e);
                }
            }}
            tabIndex={0}
        >
            <div className="text-nowrap">{title}</div>

            <div className="pl-10 hover_icon">{hoverIcon}</div>
            <div className="pl-10 default_icon">{icon}</div>
        </MenuItem>
    );
}

const forwardRefKCFMenuItem = forwardRef(KCFMenuItem);

export { forwardRefKCFMenuItem as ActionMenuItem };
